/**
 * Canonical audit-issue catalog (spec-13 §5).
 *
 * Backend code remains authoritative for detection and status; this catalog is
 * authoritative for presentation: which locale keys, expected target, allowed
 * message parameters, and supplier visibility each stable code has.
 *
 * `scripts/issue-catalog-audit.mjs` compares this file against the backend
 * code inventory and all three locale files and exits nonzero on drift — keep
 * the exported const arrays statically analyzable (plain string literals).
 */

import type { IssueSeverity, NavigationIntentKind } from './types';

export interface IssueCatalogEntry {
  /** Default severity when the source does not provide one. */
  defaultSeverity: IssueSeverity;
  /** Expected navigation intent kind (null = no navigable target). */
  targetKind: NavigationIntentKind | null;
  /** Message parameter allowlist (everything else is dropped). */
  allowedParams: readonly string[];
  /** Whether suppliers may see this code at all. */
  supplierVisible: boolean;
  /** Supplier-safe parameter subset (defaults to none). */
  supplierParams?: readonly string[];
  /** Default affected-section slug (localized via efs.issues.sections.*). */
  sectionKey?: string;
  /** Manual (operator-filed) code family: description renders as a note. */
  manual?: boolean;
}

/**
 * Every system issue code the backend can produce or store. Union of the
 * mass-balance engine taxonomy, the engine-only spec codes, and the
 * FR-008 reportable taxonomy (spec-13 §5.3).
 */
export const ISSUE_CODES = [
  'OPENING_BALANCE_NOT_CONFIRMED',
  'UNAPPROVED_INCOMING_ITEM',
  'UNAPPROVED_MATERIAL',
  'PENDING_NORMALIZATION',
  'MISSING_SORTING_DECISION',
  'UNCONFIRMED_DISMANTLING_SESSION',
  'UNAPPROVED_SECONDARY_DESTINATION',
  'MISSING_SECONDARY_RECEIPT',
  'UNAPPROVED_SHIPMENT_DESTINATION',
  'MISSING_SHIPMENT_RECEIPT',
  'OUTBOUND_RECEIPT_NOT_CONFIRMED',
  'OUTBOUND_RESOURCE_CHAIN_NOT_CONFIRMED',
  'LOSS_DEDUCTION_NOT_APPROVED',
  'NEGATIVE_INVENTORY',
  'SHIPMENT_EXCEEDS_AVAILABLE_INVENTORY',
  'OUTBOUND_EXCEEDS_AVAILABLE_INVENTORY',
  'SUMMARY_CATEGORY_MISMATCH',
  'SUMMARY_TOTAL_MISMATCH',
  'MASS_BALANCE_WARNING',
  'MASS_BALANCE_OUT_OF_TOLERANCE',
  'POST_CLOSE_EDIT_ATTEMPT',
  'DISMANTLING_OUTPUT_AMENDED',
  'quality_difference',
  'packaging_difference',
  'wrong_shipment',
  'quantity_abnormality',
  'weight_difference',
  'other',
] as const;

export const MONTHLY_REPORT_PRECHECK_CODES = [
  'MONTHLY_REPORT_BATCH_NOT_FOUND',
  'MONTHLY_REPORT_UNCONFIRMED_INBOUND',
  'MONTHLY_REPORT_UNCONFIRMED_DISMANTLING',
  'MONTHLY_REPORT_UNCONFIRMED_OUTBOUND',
  'MONTHLY_REPORT_RECONCILIATION_MISSING',
  'MONTHLY_REPORT_RECONCILIATION_BLOCKED',
  'MONTHLY_REPORT_PROFILE_MISSING',
  'MONTHLY_REPORT_PROFILE_NOT_LOCKED',
  'MONTHLY_REPORT_LOGO_UNAVAILABLE',
  'MONTHLY_REPORT_COMMERCIAL_TERMS_MISSING',
  'MONTHLY_REPORT_COMMERCIAL_TERMS_NOT_LOCKED',
  'MONTHLY_REPORT_LOCKED_RATE_MISSING',
  'MONTHLY_REPORT_RECIPIENT_MISSING',
  'MONTHLY_REPORT_RECIPIENT_REQUIRED',
  'MONTHLY_REPORT_CROSS_MONTH',
  'MONTHLY_REPORT_ARTIFACT_STORAGE_MISMATCH',
  'MONTHLY_REPORT_SOURCE_CHANGED',
] as const;

export type IssueCode =
  | (typeof ISSUE_CODES)[number]
  | (typeof MONTHLY_REPORT_PRECHECK_CODES)[number];

const MANUAL_DEFAULTS = {
  defaultSeverity: 'warning',
  targetKind: 'monthly_batch',
  allowedParams: [],
  supplierVisible: true,
  manual: true,
} satisfies IssueCatalogEntry;

export const ISSUE_CATALOG: Record<IssueCode, IssueCatalogEntry> = {
  OPENING_BALANCE_NOT_CONFIRMED: {
    defaultSeverity: 'error',
    targetKind: 'batch_opening_balance',
    allowedParams: [],
    supplierVisible: false,
    sectionKey: 'opening_balance',
  },
  UNAPPROVED_INCOMING_ITEM: {
    defaultSeverity: 'error',
    targetKind: 'master_incoming_item',
    allowedParams: ['name'],
    supplierVisible: false,
    sectionKey: 'masters',
  },
  UNAPPROVED_MATERIAL: {
    defaultSeverity: 'error',
    targetKind: 'master_material',
    allowedParams: ['name'],
    supplierVisible: false,
    sectionKey: 'masters',
  },
  PENDING_NORMALIZATION: {
    defaultSeverity: 'error',
    targetKind: 'master_incoming_item',
    allowedParams: ['name'],
    supplierVisible: false,
    sectionKey: 'imports',
  },
  MISSING_SORTING_DECISION: {
    defaultSeverity: 'error',
    targetKind: 'batch_inbound_lot',
    allowedParams: ['lot_code'],
    supplierVisible: false,
    sectionKey: 'sorting',
  },
  UNCONFIRMED_DISMANTLING_SESSION: {
    defaultSeverity: 'error',
    targetKind: 'batch_dismantling',
    allowedParams: [],
    supplierVisible: false,
    sectionKey: 'dismantling',
  },
  UNAPPROVED_SECONDARY_DESTINATION: {
    defaultSeverity: 'critical',
    targetKind: 'master_destination',
    allowedParams: ['name'],
    supplierVisible: false,
    sectionKey: 'masters',
  },
  MISSING_SECONDARY_RECEIPT: {
    defaultSeverity: 'error',
    targetKind: 'batch_outbounds',
    allowedParams: [],
    supplierVisible: false,
    sectionKey: 'secondary_process',
  },
  UNAPPROVED_SHIPMENT_DESTINATION: {
    defaultSeverity: 'critical',
    targetKind: 'master_destination',
    allowedParams: ['name'],
    supplierVisible: false,
    sectionKey: 'masters',
  },
  MISSING_SHIPMENT_RECEIPT: {
    defaultSeverity: 'error',
    targetKind: 'batch_outbounds',
    allowedParams: [],
    supplierVisible: false,
    sectionKey: 'outbounds',
  },
  OUTBOUND_RECEIPT_NOT_CONFIRMED: {
    defaultSeverity: 'error',
    targetKind: 'batch_outbounds',
    allowedParams: [],
    supplierVisible: false,
    sectionKey: 'outbounds',
  },
  OUTBOUND_RESOURCE_CHAIN_NOT_CONFIRMED: {
    defaultSeverity: 'error',
    targetKind: 'batch_outbounds',
    allowedParams: [],
    supplierVisible: false,
    sectionKey: 'outbounds',
  },
  LOSS_DEDUCTION_NOT_APPROVED: {
    defaultSeverity: 'error',
    targetKind: 'batch_approvals',
    allowedParams: [],
    supplierVisible: false,
    sectionKey: 'inventory',
  },
  NEGATIVE_INVENTORY: {
    defaultSeverity: 'critical',
    targetKind: 'batch_inventory',
    allowedParams: ['name', 'running_balance_kg'],
    supplierVisible: false,
    sectionKey: 'inventory',
  },
  SHIPMENT_EXCEEDS_AVAILABLE_INVENTORY: {
    defaultSeverity: 'critical',
    targetKind: 'batch_outbounds',
    allowedParams: ['name'],
    supplierVisible: false,
    sectionKey: 'outbounds',
  },
  OUTBOUND_EXCEEDS_AVAILABLE_INVENTORY: {
    defaultSeverity: 'critical',
    targetKind: 'batch_inventory',
    allowedParams: ['name', 'outbound_total_kg', 'available_kg'],
    supplierVisible: false,
    sectionKey: 'outbounds',
  },
  SUMMARY_CATEGORY_MISMATCH: {
    defaultSeverity: 'warning',
    targetKind: 'batch_mass_balance',
    allowedParams: ['category', 'table', 'diff_kg'],
    supplierVisible: false,
    sectionKey: 'reconciliation',
  },
  SUMMARY_TOTAL_MISMATCH: {
    defaultSeverity: 'error',
    targetKind: 'batch_mass_balance',
    allowedParams: ['diff_kg'],
    supplierVisible: false,
    sectionKey: 'reconciliation',
  },
  MASS_BALANCE_WARNING: {
    defaultSeverity: 'warning',
    targetKind: 'batch_mass_balance',
    allowedParams: ['scope', 'material_name', 'diff'],
    supplierVisible: false,
    sectionKey: 'mass_balance',
  },
  MASS_BALANCE_OUT_OF_TOLERANCE: {
    defaultSeverity: 'error',
    targetKind: 'batch_mass_balance',
    allowedParams: ['scope', 'material_name', 'diff'],
    supplierVisible: false,
    sectionKey: 'mass_balance',
  },
  POST_CLOSE_EDIT_ATTEMPT: {
    defaultSeverity: 'critical',
    targetKind: 'monthly_batch',
    allowedParams: [],
    supplierVisible: false,
    sectionKey: 'audit_history',
  },
  DISMANTLING_OUTPUT_AMENDED: {
    defaultSeverity: 'warning',
    targetKind: 'batch_dismantling',
    // `reason` is the operator-authored amendment reason (shown internally).
    allowedParams: ['reason'],
    supplierVisible: false,
    sectionKey: 'dismantling',
  },
  quality_difference: { ...MANUAL_DEFAULTS },
  packaging_difference: { ...MANUAL_DEFAULTS },
  wrong_shipment: { ...MANUAL_DEFAULTS },
  quantity_abnormality: { ...MANUAL_DEFAULTS },
  weight_difference: { ...MANUAL_DEFAULTS },
  other: { ...MANUAL_DEFAULTS },
  MONTHLY_REPORT_BATCH_NOT_FOUND: {
    defaultSeverity: 'error',
    targetKind: 'monthly_batch',
    allowedParams: ['monthly_batch_id'],
    supplierVisible: false,
    sectionKey: 'monthly_report',
  },
  MONTHLY_REPORT_UNCONFIRMED_INBOUND: {
    defaultSeverity: 'error',
    targetKind: 'batch_inbound_lot',
    allowedParams: ['lot_code', 'expected_status'],
    supplierVisible: true,
    supplierParams: ['lot_code'],
    sectionKey: 'inbound',
  },
  MONTHLY_REPORT_UNCONFIRMED_DISMANTLING: {
    defaultSeverity: 'error',
    targetKind: 'batch_dismantling',
    allowedParams: ['record_code', 'expected_status'],
    supplierVisible: false,
    sectionKey: 'dismantling',
  },
  MONTHLY_REPORT_UNCONFIRMED_OUTBOUND: {
    defaultSeverity: 'error',
    targetKind: 'batch_outbounds',
    allowedParams: ['record_code', 'expected_status'],
    supplierVisible: false,
    sectionKey: 'outbounds',
  },
  MONTHLY_REPORT_RECONCILIATION_MISSING: {
    defaultSeverity: 'error',
    targetKind: 'batch_mass_balance',
    allowedParams: ['year_month'],
    supplierVisible: false,
    sectionKey: 'mass_balance',
  },
  MONTHLY_REPORT_RECONCILIATION_BLOCKED: {
    defaultSeverity: 'error',
    targetKind: 'batch_mass_balance',
    allowedParams: ['diff_kg', 'status'],
    supplierVisible: false,
    sectionKey: 'mass_balance',
  },
  MONTHLY_REPORT_PROFILE_MISSING: {
    defaultSeverity: 'error',
    targetKind: 'monthly_batch',
    allowedParams: ['variant'],
    supplierVisible: false,
    sectionKey: 'monthly_report',
  },
  MONTHLY_REPORT_PROFILE_NOT_LOCKED: {
    defaultSeverity: 'error',
    targetKind: 'monthly_batch',
    allowedParams: ['variant'],
    supplierVisible: false,
    sectionKey: 'monthly_report',
  },
  MONTHLY_REPORT_LOGO_UNAVAILABLE: {
    defaultSeverity: 'error',
    targetKind: 'monthly_batch',
    allowedParams: [],
    supplierVisible: false,
    sectionKey: 'monthly_report',
  },
  MONTHLY_REPORT_COMMERCIAL_TERMS_MISSING: {
    defaultSeverity: 'error',
    targetKind: 'monthly_batch',
    allowedParams: ['variant'],
    supplierVisible: false,
    sectionKey: 'settlement',
  },
  MONTHLY_REPORT_COMMERCIAL_TERMS_NOT_LOCKED: {
    defaultSeverity: 'error',
    targetKind: 'monthly_batch',
    allowedParams: ['variant'],
    supplierVisible: false,
    sectionKey: 'settlement',
  },
  MONTHLY_REPORT_LOCKED_RATE_MISSING: {
    defaultSeverity: 'error',
    targetKind: 'monthly_batch',
    allowedParams: ['variant', 'missing_field'],
    supplierVisible: false,
    sectionKey: 'settlement',
  },
  MONTHLY_REPORT_RECIPIENT_MISSING: {
    defaultSeverity: 'error',
    targetKind: 'monthly_batch',
    allowedParams: ['variant', 'recipient_company_id'],
    supplierVisible: false,
    sectionKey: 'settlement',
  },
  MONTHLY_REPORT_RECIPIENT_REQUIRED: {
    defaultSeverity: 'error',
    targetKind: 'monthly_batch',
    allowedParams: ['variant'],
    supplierVisible: false,
    sectionKey: 'monthly_report',
  },
  MONTHLY_REPORT_CROSS_MONTH: {
    defaultSeverity: 'error',
    targetKind: 'batch_inbound_lot',
    allowedParams: ['lot_code'],
    supplierVisible: true,
    supplierParams: ['lot_code'],
    sectionKey: 'inbound',
  },
  MONTHLY_REPORT_ARTIFACT_STORAGE_MISMATCH: {
    defaultSeverity: 'critical',
    targetKind: 'monthly_batch',
    allowedParams: ['artifact_id'],
    supplierVisible: false,
    sectionKey: 'monthly_report',
  },
  MONTHLY_REPORT_SOURCE_CHANGED: {
    defaultSeverity: 'warning',
    targetKind: 'monthly_batch',
    allowedParams: ['variant'],
    supplierVisible: false,
    sectionKey: 'monthly_report',
  },
};

/** Blocker-dashboard categories (spec-13 §5.4; backend dashboard/service.rs). */
export const BLOCKER_CATEGORIES = [
  'first_valuation_unlocked',
  'material_valuation_unlocked',
  'getit_purchase_price_unlocked',
  'ef_dk_settlement_unlocked',
  'external_report_missing',
  'internal_report_missing',
  'reconciliation_not_run',
  'reconciliation_blocked',
  'open_critical_exceptions',
  'open_error_exceptions',
  'overdue_unconfirmed',
  'opening_balance_unconfirmed',
  'evidence_missing',
  'monthly_close_unapproved',
  'not_submitted_for_close',
] as const;

export type BlockerCategory = (typeof BLOCKER_CATEGORIES)[number];

/** Per-category batch destination inside the batch (nearest useful section). */
export const BLOCKER_CATEGORY_SECTIONS: Record<BlockerCategory, string | null> = {
  first_valuation_unlocked: 'first-valuation',
  material_valuation_unlocked: 'valuation/monthly-material',
  getit_purchase_price_unlocked: 'valuation/getit-purchase-price',
  ef_dk_settlement_unlocked: 'settlement',
  external_report_missing: 'reports',
  internal_report_missing: 'reports',
  reconciliation_not_run: 'mass-balance',
  reconciliation_blocked: 'mass-balance',
  open_critical_exceptions: 'exceptions',
  open_error_exceptions: 'exceptions',
  overdue_unconfirmed: null,
  opening_balance_unconfirmed: 'opening-balance',
  evidence_missing: 'evidence',
  monthly_close_unapproved: 'approvals',
  not_submitted_for_close: 'mass-balance',
};

/** In-app notification event codes (backend notifications inventory). */
export const NOTIFICATION_EVENT_CODES = [
  'exception.supplier_reply',
  'exception.resolved',
  'exception.waived',
  'exception.comment',
  'exception.assigned',
  'monthly_close.delay_confirmed',
  'monthly_close.deadline_approaching',
] as const;

export type NotificationEventCode = (typeof NOTIFICATION_EVENT_CODES)[number];

/** Events whose stored `body` is operator-authored content (render as note). */
export const NOTIFICATION_AUTHORED_BODY_EVENTS: readonly NotificationEventCode[] = [
  'exception.supplier_reply',
  'exception.resolved',
  'exception.waived',
  'exception.comment',
];

/** Import validation codes (backend imports inventory). */
export const IMPORT_VALIDATION_CODES = [
  'SHEET_SKIPPED',
  'MISSING_LOT_CODE',
  'MISSING_ITEM_NAME',
  'INVALID_KG',
  'INVALID_PRICE',
  'SUMMARY_TOTAL_MISMATCH',
  'DISMANTLING_SESSION_DATE_MISSING',
  'DISMANTLING_WORKER_COUNT_MISSING',
  'DISMANTLING_METHOD_MISSING',
  'SKIPPED_SUBTOTAL_ROW',
  'UNKNOWN_MATERIAL',
  'UNKNOWN_INCOMING_ITEM',
  'NORMALIZED_BY_RULE',
  'DUPLICATE_LOT_CODE',
] as const;

export type ImportValidationCode = (typeof IMPORT_VALIDATION_CODES)[number];

/**
 * Finite, audited registry of import-validation FIELD keys (spec-13 §8.5 /
 * round-5 item 8): every `.with_field("…")` the backend producers emit must
 * appear here and resolve in all three locales (`efs.issues.import_fields.*`).
 * Unknown field keys render the localized unavailable label; the raw key is
 * technical-only.
 */
export const IMPORT_VALIDATION_FIELD_KEYS = [
  'lot_code',
  'item_name',
  'kg',
  'unit_price',
  'session_date',
  'worker_count',
  'method',
  'material_name',
] as const;

export type ImportValidationFieldKey = (typeof IMPORT_VALIDATION_FIELD_KEYS)[number];

export const isImportValidationField = (
  field: string | null | undefined,
): field is ImportValidationFieldKey =>
  !!field && (IMPORT_VALIDATION_FIELD_KEYS as readonly string[]).includes(field);

export const importFieldLabelKey = (field: ImportValidationFieldKey): string =>
  `efs.issues.import_fields.${field}`;

/** Audit-package precheck keys (backend package_export inventory). */
export const AUDIT_PACKAGE_CHECK_KEYS = [
  'batch_closed',
  'no_blocking_exceptions',
  'required_evidence',
  'reconciliation_snapshot_exists',
  'close_approval_present',
  'all_destinations_approved',
  'referenced_files_on_disk',
] as const;

/**
 * Stable API error codes the shared formatter can localize (backend
 * `error.rs` wire codes plus the domain-conflict codes and the two
 * client-synthesized codes).
 */
export const API_ERROR_CODES = [
  'CLI_ARGUMENT_ERROR',
  'MAINTENANCE_ERROR',
  'VALIDATION_ERROR',
  'PERMISSION_DENIED',
  'UNAUTHENTICATED',
  'BATCH_LOCKED',
  'UNAPPROVED_MASTER',
  'PENDING_NORMALIZATION',
  'MISSING_EVIDENCE',
  'MASS_BALANCE_BLOCKED',
  'DUPLICATE_SOURCE_FILE',
  'IMPORT_JOB_NOT_READY',
  'DESTINATION_NOT_APPROVED',
  'SHIPMENT_EXCEEDS_INVENTORY',
  'NOT_FOUND',
  'CONFLICT',
  'GONE',
  'SERVICE_UNAVAILABLE',
  'NOT_IMPLEMENTED',
  'INTERNAL_ERROR',
  'DATABASE_ERROR',
  'NETWORK_ERROR',
  'UNKNOWN_ERROR',
  // Stable operational domain codes (spec-13 §8.7/§10.1).
  'DISPATCH_STATUS_CONFLICT',
  'DISPATCH_ROUTED_FACILITY_MISSING',
  'DISPATCH_ALREADY_ACCEPTED',
  'DISPATCH_LINKED_INBOUND_CONFLICT',
  'BUYER_PROPOSAL_VENDOR_INELIGIBLE',
  'DESTINATION_NAME_AMBIGUOUS',
  'DISMANTLING_PACKAGE_IDS_INVALID',
  'GETIT_PURCHASE_PRICE_NOT_LOCKED',
  'TRUCK_PHOTO_UNCLASSIFIED',
  'TRUCK_PHOTO_INVALID_CLASSIFICATION',
  'TRUCK_PHOTO_DIRECTION_MISSING',
  'TRUCK_PHOTO_DUPLICATE',
  'TRUCK_PHOTO_REMOVAL_BREAKS_COVERAGE',
  'TRUCK_PHOTO_REMOVAL_NOT_DUPLICATE',
  'TRUCK_PHOTO_REMOVAL_DOES_NOT_REDUCE_DUPLICATE',
  'INBOUND_LOT_ALREADY_CONFIRMED',
  'INBOUND_PACKAGES_PENDING',
  'INBOUND_LOT_NOT_CONFIRMABLE',
  'INBOUND_DELETE_LINKED_DISPATCH',
  'INBOUND_DELETE_BLOCKED',
  'INBOUND_CANCEL_BLOCKED',
  'INBOUND_RESTORE_UNSUPPORTED_SNAPSHOT',
  'INBOUND_RESTORE_BLOCKED',
  'INBOUND_RESTORE_SCHEMA_INCOMPATIBLE',
  'INBOUND_ARCHIVE_PURGE_UNSAFE_PATH',
  'DISMANTLING_CANCEL_BLOCKED',
  'DISMANTLING_RESTORE_BLOCKED',
  'DISMANTLING_RESTORE_UNSUPPORTED_SNAPSHOT',
  'DISMANTLING_RESTORE_SCHEMA_INCOMPATIBLE',
  'DISPATCH_DELETE_BLOCKED',
  'DISPATCH_RESTORE_BLOCKED',
  'DISPATCH_GROUP_DELETE_BLOCKED',
  'DISPATCH_GROUP_RESTORE_BLOCKED',
  'INVENTORY_ADJUSTMENT_NEGATIVE_BALANCE',
  'INVENTORY_MOVEMENT_DIRECT_INSERT_DISABLED',
  'MONTHLY_BATCH_PURGE_BLOCKED',
  'AUDIT_PACKAGE_BATCH_NOT_CLOSED',
  'AUDIT_PACKAGE_PRECHECK_FAILED',
  'MONTHLY_REPORT_ARTIFACT_REQUIRED',
  'MONTHLY_REPORT_ARTIFACTS_DISABLED',
  'MONTHLY_REPORT_LOGO_UNAVAILABLE',
  'MONTHLY_REPORT_RECIPIENT_MISSING',
  'MONTHLY_REPORT_RECIPIENT_REQUIRED',
  'MONTHLY_REPORT_CROSS_MONTH',
  'MONTHLY_REPORT_PRECHECK_FAILED',
  'MONTHLY_REPORT_ARTIFACT_STORAGE_MISMATCH',
  'MONTHLY_REPORT_SOURCE_CHANGED',
  'MONTHLY_REPORT_EXTERNAL_MONEY_LEAK',
  'MONTHLY_REPORT_RENDER_FAILED',
  'EXCEPTION_TARGET_INVALID',
  'CORRECTION_TARGET_INVALID',
  'CORRECTION_FIELDS_INVALID',
  'CORRECTION_NEW_VALUE_INVALID',
  'CORRECTION_TARGET_UNSUPPORTED',
  'CORRECTION_FIELD_UNSUPPORTED',
  'CORRECTION_FIELD_INVALID',
  'CORRECTION_RECOMPUTE_UNAVAILABLE',
  'CORRECTION_STALE',
  'CORRECTION_SNAPSHOT_MISSING',
  'CORRECTION_TARGET_LINKED_RECLASSIFIED_RECEIPT',
  'WEIGHT_DIFFERENCE_CHECK_SUPERSEDED',
  'IMPORT_SOURCE_UNREADABLE',
  'IMPORT_PARSE_FAILED',
  'IMPORT_MASTER_SCOPE_INVALID',
  'TENANT_SCOPE_REQUIRED',
  'SOURCE_FILE_UPLOAD_INVALID_MULTIPART',
  'SOURCE_FILE_STORAGE_UNAVAILABLE',
  'EVIDENCE_UPLOAD_INVALID_MULTIPART',
  'EVIDENCE_STORAGE_UNAVAILABLE',
  'FILE_LIBRARY_UPLOAD_INVALID_MULTIPART',
  'FILE_LIBRARY_STORAGE_UNAVAILABLE',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** Per-error-code interpolation allowlist from `error.details`. */
export const API_ERROR_DETAIL_PARAMS: Partial<Record<ApiErrorCode, readonly string[]>> = {
  DISPATCH_STATUS_CONFLICT: ['current_status', 'expected_status', 'action'],
  DISPATCH_ROUTED_FACILITY_MISSING: ['missing_field', 'action'],
  TRUCK_PHOTO_DIRECTION_MISSING: ['missing_directions'],
  TRUCK_PHOTO_REMOVAL_BREAKS_COVERAGE: ['missing_direction'],
  INBOUND_PACKAGES_PENDING: ['pending_count'],
  AUDIT_PACKAGE_PRECHECK_FAILED: ['blocking_reasons'],
  MONTHLY_REPORT_RECIPIENT_REQUIRED: ['variant'],
  MONTHLY_REPORT_CROSS_MONTH: ['lot_code'],
  MONTHLY_REPORT_PRECHECK_FAILED: ['variant'],
  MONTHLY_REPORT_SOURCE_CHANGED: ['variant'],
  MONTHLY_REPORT_ARTIFACT_STORAGE_MISMATCH: ['artifact_id'],
  MONTHLY_REPORT_EXTERNAL_MONEY_LEAK: ['field_key'],
  SOURCE_FILE_UPLOAD_INVALID_MULTIPART: ['field'],
  SOURCE_FILE_STORAGE_UNAVAILABLE: ['resource'],
  EVIDENCE_UPLOAD_INVALID_MULTIPART: ['field'],
  EVIDENCE_STORAGE_UNAVAILABLE: ['resource'],
  FILE_LIBRARY_UPLOAD_INVALID_MULTIPART: ['field'],
  FILE_LIBRARY_STORAGE_UNAVAILABLE: ['resource'],
};

/** Section slugs with localized labels (efs.issues.sections.*). */
export const ISSUE_SECTION_KEYS = [
  'opening_balance',
  'imports',
  'inbound',
  'sorting',
  'dismantling',
  'secondary_process',
  'outbounds',
  'inventory',
  'evidence',
  'reconciliation',
  'mass_balance',
  'settlement',
  'monthly_report',
  'audit_history',
  'approvals',
  'masters',
  'truck_photos',
] as const;

/** Field slugs with localized labels (efs.issues.fields.*). */
export const ISSUE_FIELD_KEYS = [
  'truck_photos.left',
  'truck_photos.right',
  'truck_photos.rear',
  'truck_photos.unclassified',
] as const;

/** Record-type slugs with localized labels (efs.issues.record_types.*). */
export const TARGET_RECORD_TYPES = [
  'monthly_batch',
  'inbound_lot',
  'inbound_package',
  'dismantling_session',
  'secondary_transfer',
  'shipment',
  'outbound',
  'material',
  'inventory_movement',
  'incoming_item',
  'destination',
  'master',
  'exception',
] as const;

export const issueCatalogEntry = (code: string): IssueCatalogEntry | null =>
  Object.prototype.hasOwnProperty.call(ISSUE_CATALOG, code)
    ? ISSUE_CATALOG[code as IssueCode]
    : null;

export const isCatalogedIssueCode = (code: string): code is IssueCode =>
  issueCatalogEntry(code) !== null;

export const isBlockerCategory = (category: string): category is BlockerCategory =>
  (BLOCKER_CATEGORIES as readonly string[]).includes(category);

export const isNotificationEventCode = (code: string): code is NotificationEventCode =>
  (NOTIFICATION_EVENT_CODES as readonly string[]).includes(code);

export const isImportValidationCode = (code: string): code is ImportValidationCode =>
  (IMPORT_VALIDATION_CODES as readonly string[]).includes(code);

export const isCatalogedApiErrorCode = (code: string): code is ApiErrorCode =>
  (API_ERROR_CODES as readonly string[]).includes(code);

/** i18n keys for one issue code (single derivation point). */
export const issueCodeKeys = (code: string) => ({
  title: `efs.issues.codes.${code}.title`,
  message: `efs.issues.codes.${code}.message`,
  action: `efs.issues.codes.${code}.action`,
});

export const blockerCategoryKeys = (category: string) => ({
  title: `efs.issues.categories.${category}.title`,
  message: `efs.issues.categories.${category}.message`,
  action: `efs.issues.categories.${category}.action`,
});

export const notificationEventKeys = (code: string) => ({
  title: `efs.issues.events.${code}.title`,
  body: `efs.issues.events.${code}.body`,
});

export const importValidationKey = (code: string) => `efs.issues.import_codes.${code}`;

export const apiErrorKey = (code: string) => `efs.issues.errors.${code}`;
